import { Plus, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useInteracaoAllList } from '@/api/queries/interacao-queries'
import {
	InspectionDetailsSection,
	ResponsiveFormModal,
} from '@/components/commons'
import { DetailField } from '@/components/commons/detail-field'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardHeader,
	InspectionDetailsHeader,
	InspectionListCardLoading,
	InspectionListFilters,
} from '@/components/inspections'
import {
	InspectionSummaryBadge,
	InspectionSummaryCard,
	InspectionSummaryCardBody,
	InspectionSummaryCardField,
	InspectionSummaryCardHeader,
	InspectionSummaryCardTitle,
} from '@/components/inspections/inspection-summary-card'
import { Button } from '@/components/ui/button'
import { CardContent, CardFooter } from '@/components/ui/card'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useAuthStore, useProfileStore } from '@/store'
import type {
	Interacao,
	InteracaoAction,
	InteracaoAllListParams,
} from '@/types/interacao'
import { capitalize } from '@/utils/capitalize'
import { formatDateToDisplay } from '@/utils/formatDate'

const actionConfig = {
	CONGRATULATED: 'inspections.interaction.actionCongratulated',
	ORIENTED: 'inspections.interaction.actionOriented',
} as const

const interactionStyle = {
	accentClassName: 'border-l-kaefer-violet',
	badgeClassName:
		'border-kaefer-violet/30 bg-kaefer-violet/15 text-kaefer-violet',
	badgeLabelKey: 'inspections.types.interaction.title',
} as const

const InteractionDetails = ({ interaction }: { interaction: Interacao }) => {
	const { t } = useTranslation()
	const actionKey = actionConfig[interaction.action]

	return (
		<div className="space-y-5 py-2">
			<InspectionDetailsHeader
				date={formatDateToDisplay(interaction.interactionDate)}
				createdBy={interaction.createdByName}
			/>

			<InspectionDetailsSection>
				<DetailField
					label={t('inspections.common.contract')}
					value={capitalize(interaction.crName)}
				/>
				<DetailField
					label={t('inspections.interaction.employee')}
					value={capitalize(interaction.employeeName)}
				/>
				<DetailField
					label={t('inspections.interaction.leader')}
					value={capitalize(interaction.leaderName)}
				/>
				{interaction.customer.id && (
					<DetailField
						label={t('inspections.common.customer')}
						value={capitalize(interaction.customer.name)}
					/>
				)}
			</InspectionDetailsSection>

			<InspectionDetailsSection>
				<DetailField
					label={t('inspections.interaction.classification')}
					value={interaction.classification}
				/>
				<DetailField
					label={t('inspections.interaction.subclassification')}
					value={interaction.subclassification}
				/>
			</InspectionDetailsSection>

			<InspectionDetailsSection layout="vertical">
				<DetailField
					label={t('inspections.interaction.description')}
					value={interaction.description}
				/>
			</InspectionDetailsSection>

			<InspectionDetailsSection showSeparator={false}>
				<DetailField
					label={t('inspections.interaction.immediateAction')}
					value={actionKey ? t(actionKey) : interaction.action}
				/>
			</InspectionDetailsSection>
		</div>
	)
}

export const InteractionListPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const userRole = useAuthStore((s) => s.user?.role)
	const { defaultBranchId } = useProfileStore()

	// Determina se usuario pode gerenciar filtros de branch
	const canManageBranchFilter =
		userRole === 'ADMIN' ||
		userRole === 'MANAGER' ||
		userRole === 'SSMA_MANAGER'

	const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>()
	const [selectedCrId, setSelectedCrId] = useState<string | undefined>(
		undefined,
	)
	const [selectedAction, setSelectedAction] = useState<
		InteracaoAction | undefined
	>()
	const [selectedCustomerId, setSelectedCustomerId] = useState<
		string | undefined
	>()
	const [search, setSearch] = useState('')
	const { debouncedValue: debouncedSearch, isPending: isSearchPending } =
		useDebouncedValue(search, 600)
	const [selectedInteraction, setSelectedInteraction] =
		useState<Interacao | null>(null)
	const [detailsOpen, setDetailsOpen] = useState(false)
	const [cursor, setCursor] = useState<string | undefined>()
	const [interactions, setInteractions] = useState<Interacao[]>([])
	const [facets, setFacets] = useState<{
		branches: Array<{ id: string; name: string; count: number }>
		crs: Array<{ id: string; name: string; count: number }>
		actions: Array<{ value: InteracaoAction; count: number }>
		customers: Array<{ id: string; name: string; count: number }>
	}>({
		branches: [],
		crs: [],
		actions: [],
		customers: [],
	})
	const [pageInfo, setPageInfo] = useState<{
		nextCursor: string | null
		hasNext: boolean
	}>({
		nextCursor: null,
		hasNext: false,
	})

	const shouldShowCustomerFilter =
		facets.customers.length > 0 || Boolean(selectedCustomerId)

	const queryParams = useMemo<InteracaoAllListParams>(
		() => ({
			// Para usuarios nao-admin, usa automaticamente a branch do perfil
			branchId: canManageBranchFilter ? selectedBranchId : defaultBranchId,
			crId: selectedCrId,
			action: selectedAction,
			customerId: shouldShowCustomerFilter ? selectedCustomerId : undefined,
			q: debouncedSearch.trim() || undefined,
			cursor,
		}),
		[
			canManageBranchFilter,
			selectedBranchId,
			defaultBranchId,
			selectedCrId,
			selectedAction,
			selectedCustomerId,
			shouldShowCustomerFilter,
			debouncedSearch,
			cursor,
		],
	)

	const { data: listData, isFetching } = useInteracaoAllList(queryParams)

	useEffect(() => {
		if (!listData) return

		setFacets({
			branches: listData.facets.branches ?? [],
			crs: listData.facets.crs ?? [],
			actions: listData.facets.actions ?? [],
			customers: listData.facets.customers ?? [],
		})
		setPageInfo(listData.pageInfo)

		setInteractions((previous) => {
			if (!cursor) {
				return listData.items
			}

			const existingIds = new Set(previous.map((interaction) => interaction.id))
			const newItems = listData.items.filter(
				(interaction) => !existingIds.has(interaction.id),
			)
			return [...previous, ...newItems]
		})
	}, [listData, cursor])

	const resetListState = () => {
		setCursor(undefined)
		setPageInfo({ nextCursor: null, hasNext: false })
	}

	const handleSearchChange = (value: string) => {
		setSearch(value)
		resetListState()
	}

	const handleBranchChange = (value: string) => {
		setSelectedBranchId(value || undefined)
		setSelectedCrId(undefined)
		setSelectedCustomerId(undefined)
		resetListState()
	}

	const handleContractChange = (value: string) => {
		setSelectedCrId(value || undefined)
		setSelectedCustomerId(undefined)
		resetListState()
	}

	const handleActionChange = (value: string) => {
		setSelectedAction((value || undefined) as InteracaoAction | undefined)
		resetListState()
	}

	const handleCustomerChange = (value: string) => {
		setSelectedCustomerId(value || undefined)
		resetListState()
	}

	useEffect(() => {
		if (shouldShowCustomerFilter || !selectedCustomerId) return
		setSelectedCustomerId(undefined)
	}, [shouldShowCustomerFilter, selectedCustomerId])

	const handleBack = () => {
		navigate(ROUTES.INSPECTIONS.INDEX)
	}

	const handleNewInteraction = () => {
		navigate(ROUTES.INSPECTIONS.INTERACTION.NEW)
	}

	const handleViewInteraction = (interaction: Interacao) => {
		setSelectedInteraction(interaction)
		setDetailsOpen(true)
	}

	const handleLoadMore = () => {
		if (!pageInfo.hasNext || !pageInfo.nextCursor || isFetching) return
		setCursor(pageInfo.nextCursor)
	}

	const colors = INSPECTION_COLORS.violet
	const hasMore = pageInfo.hasNext && Boolean(pageInfo.nextCursor)
	const isLoadingMore = isFetching && Boolean(cursor)
	const isInitialLoading = interactions.length === 0 && !listData
	const footerCountLabel =
		interactions.length === 1
			? t('inspections.interaction.countSingular')
			: t('inspections.interaction.countPlural')
	const footerText = `${hasMore ? `${interactions.length}+` : interactions.length} ${footerCountLabel}`

	const filterSelects = [
		// Filtro de Branch: visivel apenas para ADMIN, MANAGER e SSMA_MANAGER
		...(canManageBranchFilter
			? [
					{
						id: 'interaction-filter-branch',
						label: t('inspections.common.branch'),
						value: selectedBranchId,
						onChange: handleBranchChange,
						options: facets.branches.map((branch) => ({
							value: branch.id,
							label: branch.name,
							count: branch.count,
						})),
						allLabel: t('inspections.interaction.filterAll'),
					},
				]
			: []),
		{
			id: 'interaction-filter-contract',
			label: t('inspections.interaction.filterByContract'),
			value: selectedCrId,
			onChange: handleContractChange,
			options: facets.crs.map((cr) => ({
				value: cr.id,
				label: cr.name,
				count: cr.count,
			})),
			allLabel: t('inspections.interaction.filterAll'),
		},
		{
			id: 'interaction-filter-action',
			label: t('inspections.interaction.immediateAction'),
			value: selectedAction,
			onChange: handleActionChange,
			options: facets.actions.map((action) => ({
				value: action.value,
				label: actionConfig[action.value]
					? t(actionConfig[action.value])
					: action.value,
				count: action.count,
			})),
			allLabel: t('inspections.interaction.filterAll'),
		},
		...(shouldShowCustomerFilter
			? [
					{
						id: 'interaction-filter-customer',
						label: t('inspections.interaction.filterByCustomer'),
						value: selectedCustomerId,
						onChange: handleCustomerChange,
						options: facets.customers.map((customer) => ({
							value: customer.id,
							label: customer.name,
							count: customer.count,
						})),
						allLabel: t('inspections.interaction.filterAll'),
					},
				]
			: []),
	]
	const renderInteractionCard = (interaction: Interacao) => {
		return (
			<InspectionSummaryCard
				key={interaction.id}
				accentClassName={interactionStyle.accentClassName}
				onClick={() => handleViewInteraction(interaction)}
			>
				<InspectionSummaryCardBody>
					<InspectionSummaryCardHeader
						badge={
							<InspectionSummaryBadge
								label={t(interactionStyle.badgeLabelKey)}
								className={interactionStyle.badgeClassName}
							/>
						}
						topMeta={
							<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
								{formatDateToDisplay(interaction.interactionDate)}
							</span>
						}
					/>

					<InspectionSummaryCardTitle>
						{capitalize(interaction.classification)}
					</InspectionSummaryCardTitle>

					<div className="flex items-center justify-between gap-2 text-xs">
						<div className="min-w-0 flex-1 space-y-2">
							<InspectionSummaryCardField
								label="Colaborador: "
								value={capitalize(
									interaction.createdByName || interaction.employeeName,
								)}
							/>
							<InspectionSummaryCardField
								label="Contrato: "
								value={capitalize(interaction.crName)}
							/>
						</div>
					</div>
				</InspectionSummaryCardBody>
			</InspectionSummaryCard>
		)
	}

	return (
		<>
			<InspectionCard colors={colors}>
				<InspectionCardHeader
					icon={<Users className="size-5" />}
					title={t('inspections.types.interaction.title')}
					subtitle={t('inspections.interaction.listSubtitle')}
					colors={colors}
					onBack={handleBack}
					actions={
						<Button
							onClick={handleNewInteraction}
							className="w-full gap-2 bg-kaefer-violet font-semibold text-white hover:bg-kaefer-violet/90 sm:w-auto"
						>
							<Plus className="size-4" />
							{t('inspections.interaction.newInteraction')}
						</Button>
					}
				/>
				<CardContent className="flex-1 space-y-4">
					<InspectionListFilters
						searchValue={search}
						onSearchChange={handleSearchChange}
						selects={filterSelects}
					/>

					{isSearchPending ? (
						<InspectionListCardLoading type="interaction" />
					) : isInitialLoading ? (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{Array.from({ length: 6 }, (_, index) => (
								<InspectionListCardLoading
									key={`interaction-loading-${index}`}
									type="interaction"
								/>
							))}
						</div>
					) : interactions.length > 0 ? (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{interactions.map((interaction) =>
								renderInteractionCard(interaction),
							)}
						</div>
					) : (
						<div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-kaefer-light-silver py-16">
							<Users className="size-10 text-kaefer-silver" />
							<p className="mt-3 text-sm text-muted-foreground">
								{t('inspections.interaction.emptyList')}
							</p>
						</div>
					)}

					{hasMore && interactions.length > 0 && (
						<div className="flex justify-center">
							<Button
								variant="outline"
								onClick={handleLoadMore}
								disabled={isLoadingMore}
								className="gap-2"
							>
								{isLoadingMore ? (
									<>
										<div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
										{t('inspections.interaction.loadingMore')}
									</>
								) : (
									<>
										<Plus className="size-4" />
										{t('inspections.interaction.loadMore')}
									</>
								)}
							</Button>
						</div>
					)}
				</CardContent>
				<CardFooter className="mt-4 border-t border-kaefer-violet/30 bg-kaefer-violet/5 py-3">
					<p className="text-xs text-muted-foreground w-full">{footerText}</p>
				</CardFooter>
			</InspectionCard>
			<ResponsiveFormModal
				open={detailsOpen}
				onOpenChange={setDetailsOpen}
				title={t('inspections.interaction.details')}
				dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-3xl"
			>
				{selectedInteraction && (
					<InteractionDetails interaction={selectedInteraction} />
				)}
			</ResponsiveFormModal>
		</>
	)
}
